import { styled } from '@linaria/react';
import { Center, Paper, Text, Title } from '@mantine/core';
import { useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { exportChart } from '../../../../helpers/image-downloader/image-downloader';
import { ChartType } from '../../../../store/financials-store/financials-store.type';
import { newCOLORS } from '../../../../styles/colors';
import { ChartDisplayTypeButtonSet } from '../../../financials/charts/shared/chart-display-type-button-set';
import { getTaskTypeText } from '../../../tasks-table/tasks-table.helpers';
import { TaskType } from '../../../tasks-table/tasks-table.type';
import { useWorkPeriodTaskTypeBreakdown } from '../../hooks/work-periods.hooks';

interface BreakdownSectionProps {
  workPeriodId: string;
}

export const BreakdownSection = ({ workPeriodId }: BreakdownSectionProps) => {
  const { data, query } = useWorkPeriodTaskTypeBreakdown({
    workPeriodId,
  });
  const chartRef = useRef<HTMLDivElement>(null!);
  const [selectedChartType, setSelectedChartType] = useState<ChartType>(ChartType.Pie);

  // Convert raw type strings to TaskType for display
  const chartData = data
    ? Object.entries(data.types).map(([type, count], index) => {
        const vibrantColors = [
          newCOLORS.blue,
          newCOLORS.green,
          newCOLORS.purple,
          newCOLORS.orange,
          newCOLORS.pink,
          newCOLORS.teal,
          newCOLORS.indigo,
          newCOLORS.violet,
          newCOLORS.magenta,
          newCOLORS.coral,
        ];
        return {
          name: getTaskTypeText(type as unknown as TaskType),
          count,
          fill: vibrantColors[index % vibrantColors.length],
        };
      })
    : [];

  if (query.isLoading) {
    return (
      <Center>
        <Text>Loading...</Text>
      </Center>
    );
  }

  if (query.isError || !data) {
    return (
      <Center>
        <Text>Error loading breakdown data</Text>
      </Center>
    );
  }

  const renderChart = () => {
    if (selectedChartType === ChartType.Pie) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              fill="#8884d8"
              label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
            >
              {chartData.map((entry: any, index: any) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [`${value} tasks`, 'Count']}
              labelFormatter={(name: string) => `Type: ${name}`}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip
            formatter={(value: number) => [`${value} tasks`, 'Count']}
            labelFormatter={(name: string) => `Type: ${name}`}
          />
          <Legend />
          <Bar dataKey="count" name="Task Count" isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Container>
      <Title order={3}>Sprint Composition Breakdown</Title>
      <Paper p="md" withBorder>
        <ChartHeader>
          <ChartDisplayTypeButtonSet
            onSetLineChart={() => setSelectedChartType(ChartType.Pie)}
            onSetBarChart={() => setSelectedChartType(ChartType.Bar)}
            selectedChartType={selectedChartType}
            onExportChart={() => exportChart(chartRef.current, 'sprint-composition-breakdown.png')}
          />
        </ChartHeader>
        <ChartContainer ref={chartRef}>{renderChart()}</ChartContainer>
        <TotalContainer>
          <Text fw={600}>Total Tasks: {data.total}</Text>
        </TotalContainer>
      </Paper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  height: 400px;
`;

const TotalContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 16px;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;
